/**
 * FileManagePage.js - 文件管理页（后台）
 * 上传图片 / PDF，展示列表，复制 URL，删除
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Upload,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
  Tooltip,
  Image,
  DatePicker,
  Row,
  Col,
} from 'antd';
import {
  InboxOutlined,
  CopyOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { adminFileApi } from '../../api/index';

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { RangePicker } = DatePicker;

// 字节转可读字符串
function formatSize(bytes) {
  if (bytes == null) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(2) + ' MB';
}

// 拼出完整可访问 URL（后端返回的是相对 /uploads/... 路径）
function fullUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return window.location.origin + url;
}

export default function FileManagePage() {
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [keyword, setKeyword] = useState('');
  const [fileType, setFileType] = useState(undefined);
  const [uploadedFrom, setUploadedFrom] = useState('');
  const [uploadedTo, setUploadedTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminFileApi.list({
        keyword: keyword || undefined,
        fileType: fileType || undefined,
        uploadedFrom: uploadedFrom || undefined,
        uploadedTo: uploadedTo || undefined,
        page,
        pageSize,
      });
      setFiles(data?.items || []);
      setTotal(data?.total || 0);
    } catch (err) {
      message.error('加载文件列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [keyword, fileType, uploadedFrom, uploadedTo, page, pageSize]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(fullUrl(url));
      message.success('URL 已复制');
    } catch (e) {
      message.error('复制失败，请手动选择 URL');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminFileApi.delete(id);
      message.success('已删除');
      fetchList();
    } catch (err) {
      message.error('删除失败：' + (err?.response?.data?.message || err.message || '未知错误'));
    }
  };

  // Upload Dragger 配置
  const draggerProps = {
    name: 'file',
    multiple: true,
    accept: '.jpg,.jpeg,.png,.gif,.webp,.pdf',
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const result = await adminFileApi.upload(file);
        onSuccess(result, file);
        message.success(`${file.name} 上传成功`);
        fetchList();
      } catch (err) {
        onError(err);
        message.error(`${file.name} 上传失败：${err?.response?.data?.message || err.message || '未知错误'}`);
      }
    },
    beforeUpload: (file) => {
      if (file.size > 20 * 1024 * 1024) {
        message.error(`${file.name} 超过 20MB，无法上传`);
        return Upload.LIST_IGNORE;
      }
      return true;
    },
  };

  const columns = [
    {
      title: '预览',
      dataIndex: 'url',
      key: 'preview',
      width: 90,
      render: (url, record) => {
        if (record.fileType === 'image') {
          return (
            <Image
              src={fullUrl(url)}
              alt={record.originalFilename}
              width={56}
              height={56}
              style={{ objectFit: 'cover', borderRadius: 4 }}
            />
          );
        }
        return (
          <a href={fullUrl(url)} target="_blank" rel="noreferrer">
            <FilePdfOutlined style={{ fontSize: 40, color: '#C44536' }} />
          </a>
        );
      },
    },
    {
      title: '文件名',
      dataIndex: 'originalFilename',
      key: 'originalFilename',
      ellipsis: true,
      render: (name, record) => (
        <Tooltip title={name}>
          <a href={fullUrl(record.url)} target="_blank" rel="noreferrer">{name}</a>
        </Tooltip>
      ),
    },
    {
      title: '类型',
      dataIndex: 'fileType',
      key: 'fileType',
      width: 80,
      render: (t) => <Tag color={t === 'image' ? 'green' : 'red'}>{t === 'image' ? '图片' : 'PDF'}</Tag>,
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
      width: 110,
      render: (v) => formatSize(v),
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url) => (
        <Text copyable={false} style={{ fontSize: 12 }}>
          <code style={{ fontSize: 12 }}>{fullUrl(url)}</code>
        </Text>
      ),
    },
    {
      title: '上传人',
      dataIndex: 'uploadedByEmail',
      key: 'uploadedByEmail',
      width: 180,
      render: (email) => email || '-',
    },
    {
      title: '上传时间',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      width: 170,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 170,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(record.url)}>
            复制
          </Button>
          <Popconfirm
            title="确认删除该文件？"
            description="磁盘文件也会同时删除，无法恢复。"
            onConfirm={() => handleDelete(record.id)}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>文件管理</Title>

      {/* 上传区 */}
      <Card style={{ marginBottom: 16 }}>
        <Dragger {...draggerProps} style={{ background: '#fafafa' }}>
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
          <p className="ant-upload-hint">
            支持图片（jpg / jpeg / png / gif / webp）和 PDF，单文件最大 20 MB，可批量上传
          </p>
        </Dragger>
      </Card>

      {/* 筛选 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Input
              placeholder="按文件名搜索"
              prefix={<SearchOutlined />}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={() => { setPage(1); fetchList(); }}
              allowClear
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="文件类型"
              allowClear
              style={{ width: '100%' }}
              value={fileType}
              onChange={(v) => { setFileType(v); setPage(1); }}
              options={[
                { value: 'image', label: '图片' },
                { value: 'pdf', label: 'PDF' },
              ]}
            />
          </Col>
          <Col xs={24} md={8}>
            <RangePicker
              style={{ width: '100%' }}
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              onChange={(range) => {
                if (range && range[0] && range[1]) {
                  setUploadedFrom(range[0].format('YYYY-MM-DDTHH:mm:ss'));
                  setUploadedTo(range[1].format('YYYY-MM-DDTHH:mm:ss'));
                } else {
                  setUploadedFrom('');
                  setUploadedTo('');
                }
                setPage(1);
              }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={() => { setPage(1); fetchList(); }}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => {
                setKeyword('');
                setFileType(undefined);
                setUploadedFrom('');
                setUploadedTo('');
                setPage(1);
              }}>
                重置
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 列表 */}
      <Card>
        <Table
          dataSource={files}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 个文件`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>
    </div>
  );
}
